import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { PartnerSpecialization } from 'clickNgo/entities/partner/models/specialization'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Tooltip } from 'core-components/library/tooltip'

import { SpecializationConfig } from './configs'
import { PartnerSpecializationIcon } from './models'
import { partnerSpecializationCarouselTranslation } from './translation'

import styles from './PartnerSpecializationCarousel.module.scss'

type Props = {
  partnerSpecializations: PartnerSpecialization[]
}

export const PartnerSpecializationCarousel = memo<Props>(({ partnerSpecializations }) => {
  const carouselRef = useRef<HTMLDivElement>(null)
  const [partnerSpecializationIcons, setPartnerSpecializationIcons] = useState<
    PartnerSpecializationIcon[]
  >([])
  const [isPrevDisabled, setIsPrevDisabled] = useState(true)
  const [isNextDisabled, setIsNextDisabled] = useState(false)
  const [isButtonsVisible, setIsButtonsVisible] = useState(false)

  const checkScrollable = useCallback(() => {
    if (carouselRef.current) {
      const { scrollWidth, clientWidth } = carouselRef.current
      setIsButtonsVisible(scrollWidth > clientWidth)
    }
  }, [])

  const handleScroll = useCallback((scrollOffset: number) => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft += scrollOffset
    }
  }, [])

  const scrollNext = useCallback(() => {
    if (carouselRef.current) {
      const firstChild = carouselRef.current.children[0] as HTMLElement
      const itemWidth = firstChild.offsetWidth + 16
      handleScroll(itemWidth)
    }
  }, [handleScroll])

  const scrollPrev = useCallback(() => {
    if (carouselRef.current) {
      const firstChild = carouselRef.current.children[0] as HTMLElement
      const itemWidth = firstChild.offsetWidth + 16
      handleScroll(-itemWidth)
    }
  }, [handleScroll])

  const handleCarouselScroll = useCallback(() => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current
      setIsPrevDisabled(scrollLeft === 0)
      setIsNextDisabled(scrollLeft >= scrollWidth - clientWidth - 16)
    }
  }, [])

  useLayoutEffect(() => {
    const specializationLabels = partnerSpecializations.map(({ name }) => name.trim())
    setPartnerSpecializationIcons(() => {
      return SpecializationConfig.filter((spec) => specializationLabels.includes(spec.label))
    })
  }, [partnerSpecializations])

  useLayoutEffect(() => {
    handleCarouselScroll()
  }, [carouselRef, handleCarouselScroll])

  // Add this useLayoutEffect to check scrollable status after icons are updated
  useLayoutEffect(() => {
    if (partnerSpecializationIcons.length) {
      checkScrollable()
    }
  }, [partnerSpecializationIcons, checkScrollable])

  useEffect(() => {
    window.addEventListener('resize', checkScrollable)

    return () => {
      window.removeEventListener('resize', checkScrollable)
    }
  }, [checkScrollable])

  if (partnerSpecializations.length === 0) {
    return null
  }

  return (
    <div className={styles.container}>
      <span className={styles.label}>
        <Translation text={partnerSpecializationCarouselTranslation.label} span />
      </span>
      <div className={styles.carouser}>
        <RenderWhen condition={isButtonsVisible}>
          <button className={styles.navButton} onClick={scrollPrev} disabled={isPrevDisabled}>
            <i className="kbase kbase-chevron-left" />
          </button>
        </RenderWhen>
        <div ref={carouselRef} className={styles.carousel} onScroll={handleCarouselScroll}>
          {partnerSpecializationIcons.map((specialization) => (
            <Tooltip key={specialization.label} tipContent={() => <>{specialization.label}</>}>
              <div key={specialization.label} className={styles.icon}>
                {specialization.icon}
              </div>
            </Tooltip>
          ))}
        </div>
        <RenderWhen condition={isButtonsVisible}>
          <button className={styles.navButton} onClick={scrollNext} disabled={isNextDisabled}>
            <i className="kbase kbase-chevron-right" />
          </button>
        </RenderWhen>
      </div>
    </div>
  )
})

PartnerSpecializationCarousel.displayName = 'PartnerSpecializationCarousel'
